import { yupResolver } from '@hookform/resolvers/yup';
import PropTypes from 'prop-types';
import { useEffect, useId, useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { FormProvider, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import Modal from 'src/components/common/modal/modal';
import Input from 'src/components/form/floating-label/input';
import Spinner from 'src/components/spinner/spinner';
import { env } from 'src/constants/env';
import API from './api/feedback-api';
import css from './feedback.module.scss';
import getValidationSchema from './validate';
import { fireEvent } from 'src/utils/analytics/google';

Feedback.propTypes = {
  trigger: PropTypes.element
};

export default function Feedback({ trigger }) {
  const validationSchema = getValidationSchema();
  const [isLoading, setIsLoading] = useState(false);
  const captchaRef = useRef(null);

  const methods = useForm({
    mode: 'onBlur',
    resolver: yupResolver(validationSchema),
    defaultValues: {
      name: '',
      email: '',
      header: '',
      description: ''
    }
  });
  const {
    register,
    handleSubmit,
    clearErrors,
    reset,
    getValues,
    formState: { errors, isSubmitSuccessful, defaultValues, isDirty, isValid }
  } = methods;
  const values = getValues();
  const isEdited = Object.keys(values).some((key) => values[key] !== defaultValues[key]);

  const registerAndClearOnChange = function (inputName) {
    return register(inputName, {
      onChange: () => {
        if (errors[inputName]) {
          clearErrors(inputName);
        }
      }
    });
  };

  useEffect(() => {
    if (isSubmitSuccessful) {
      reset();
      captchaRef.current?.reset();
    }
  }, [isSubmitSuccessful, reset]);

  const onFormSubmit = async (data, close) => {
    setIsLoading(true);
    fireEvent('feedback_form_submit', data);
    try {
      const response = await API.feedback(data);

      if (response.status === 201) {
        toast.success('Дякуємо! Ми отримали Ваш відгук і опрацюємо його.');
        close();
      }
    } catch (error) {
      toast.error('Виникла помилка. Спробуйте, будь ласка, пізніше.');
    }
    setIsLoading(false);
  };

  const nameInputId = useId();
  const emailInputId = useId();
  const headerInputId = useId();
  const descriptionInputId = useId();

  return (
    <Modal trigger={trigger} onClose={reset} nested closeOnDocumentClick={!isEdited}>
      {(close) => (
        <div className={css.feedback}>
          <FormProvider {...methods}>
            <form
              onSubmit={handleSubmit(async (data) => {
                const token = await captchaRef.current?.executeAsync();
                onFormSubmit({ ...data, captcha: token }, close);
              })}
              className={css.form}
              autoComplete='off'>
              <legend>
                <h1>Залиште свій відгук</h1>
              </legend>
              <fieldset>
                <Input
                  id={nameInputId}
                  label="Ім'я"
                  error={errors?.name?.message}
                  {...registerAndClearOnChange('name')}
                />
                <Input
                  id={emailInputId}
                  label='Ел. пошта'
                  placeholder='example@email.com'
                  error={errors?.email?.message}
                  {...registerAndClearOnChange('email')}
                />
                <Input
                  id={headerInputId}
                  label='Тема листа'
                  error={errors?.header?.message}
                  {...registerAndClearOnChange('header')}
                />
                <Input
                  id={descriptionInputId}
                  label='Текст повідомлення'
                  multiline
                  error={errors?.description?.message}
                  required
                  {...registerAndClearOnChange('description')}
                />
              </fieldset>

              <button className={css.button} disabled={!isDirty || !isValid} type='submit'>
                {isLoading ? <Spinner /> : 'Надіслати'}
              </button>
            </form>
            <ReCAPTCHA
              ref={captchaRef}
              className={css.captcha}
              sitekey={env.REACT_APP_NZ_RECAPTCHA_SITE_KEY}
              hl='uk'
              size='invisible'
            />
          </FormProvider>
          <button className={css.close} onClick={close} tabIndex={0}>
            &times;
          </button>
        </div>
      )}
    </Modal>
  );
}
